import React, { useState } from "react";
import {
  useTable,
  List,
  EditButton,
  ShowButton,
  DeleteButton,
  CreateButton,
} from "@refinedev/antd";
import { Table, Space, Tag, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { DELETE_SOURCING_MUTATION, SOURCING_LIST_QUERY } from "./queries";
import { SourcingStatus, SourcingType, InvitationMode } from "./interface";

interface ISourcingRow {
  id: string;
  sourcingNo: string;
  prIds: string[];
  type: SourcingType;
  invitationMode: InvitationMode;
  status: SourcingStatus;
  totalAmount: number;
  currency?: string;
  createdAt: string;
  updatedAt: string;
}

const StatusBadge: React.FC<{ status: SourcingStatus }> = ({ status }) => {
  const colorMap: Record<SourcingStatus, string> = {
    DRAFT: "default",
    SUBMITTED: "processing",
    APPROVED: "success",
    REJECTED: "error",
    PUBLISHED: "purple",
    CLOSED: "orange",
    [SourcingStatus.RESULT_SUBMITTED]: "cyan",
    [SourcingStatus.AWARD_APPROVED]: "",
    [SourcingStatus.PARTIALLY_AWARDED]: "blue",
    [SourcingStatus.AWARDED]: ""
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

const TypeBadge: React.FC<{ type: SourcingType }> = ({ type }) => {
  const colorMap: Record<SourcingType, string> = {
    RFQ: "blue",
    RFP: "purple",
  };
  return <Tag color={colorMap[type] || "default"}>{type}</Tag>;
};

const ModeBadge: React.FC<{ mode: InvitationMode }> = ({ mode }) => {
  const colorMap: Record<InvitationMode, string> = {
    CLOSED: "orange",
    OPEN: "green",
  };
  return <Tag color={colorMap[mode] || "default"}>{mode}</Tag>;
};

export const SourcingList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<SourcingStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<SourcingType | "">("");
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  const { tableProps } = useTable<ISourcingRow>({
    resource: "sourcings",
    initialSorter: [{ field: "id", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: SOURCING_LIST_QUERY,
    },
    filters: {
      permanent: [
        ...(statusFilter
          ? [{ field: "status", operator: "eq" as const, value: statusFilter }]
          : []),
        ...(typeFilter
          ? [{ field: "type", operator: "eq" as const, value: typeFilter }]
          : []),
      ],
    },
    sorters: {
      initial: [
        {
          field: "id",
          order: "asc",
        },
      ],
    },
  });

  // Debug: Log the current sorting state
  console.log("Current tableProps:", tableProps);
  console.log("Current sorting from tableProps:", (tableProps as any)?.sorting);

  const dataSource: ISourcingRow[] = (
    (tableProps as any)?.dataSource || []
  ).filter(
    (row: ISourcingRow) =>
      !searchTerm ||
      row.sourcingNo?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.prIds?.some(id => id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by Sourcing No or PR ID..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 280 }}
            />
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140 }}
              allowClear
            >
              <Select.Option value="DRAFT">Draft</Select.Option>
              <Select.Option value="SUBMITTED">Submitted</Select.Option>
              <Select.Option value="APPROVED">Approved</Select.Option>
              <Select.Option value="REJECTED">Rejected</Select.Option>
            </Select>
            <Select
              placeholder="Filter by Type"
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value="RFQ">RFQ</Select.Option>
              <Select.Option value="RFP">RFP</Select.Option>
            </Select>
            <CreateButton />
           
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource as any} rowKey="id" key={forceUpdateKey}>
        <Table.Column dataIndex="sourcingNo" title="Sourcing No" sorter />
        <Table.Column 
          title="PR References" 
          render={(record) => (
            <div>
              {record.prIds && record.prIds.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {record.prIds.slice(0, 2).map((prId: string) => (
                    <li key={prId} style={{ fontSize: '12px' }}>{prId}</li>
                  ))}
                  {record.prIds.length > 2 && (
                    <li style={{ fontSize: '12px', color: '#999' }}>+{record.prIds.length - 2} more</li>
                  )}
                </ul>
              ) : (
                'N/A'
              )}
            </div>
          )}
        />
        <Table.Column
          dataIndex="type"
          title="Type"
          render={(type: SourcingType) => <TypeBadge type={type} />}
          sorter
        />
        <Table.Column
          dataIndex="invitationMode"
          title="Invitation Mode"
          render={(mode: InvitationMode) => <ModeBadge mode={mode} />}
          sorter
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: SourcingStatus) => <StatusBadge status={status} />}
          sorter
        />
        <Table.Column
          dataIndex="totalAmount"
          title="Total Amount"
          sorter
          render={(value: number, record: ISourcingRow) =>
            `${record.currency || "ETB"} ${Number(value ?? 0).toLocaleString()}`
          }
        />
        <Table.Column dataIndex="createdAt" title="Created Date" sorter />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: ISourcingRow) => (
            <Space>
              <EditButton
                size="small"
                recordItemId={record.id}
                resource="sourcings"
                disabled={record.status !== "DRAFT"}
              />
              {record.status === "DRAFT" && (
                <DeleteButton
                  size="small"
                  recordItemId={record.id}
                  meta={{ gqlMutation: DELETE_SOURCING_MUTATION }}
                  resource="sourcings"
                />
              )}
              <ShowButton
                size="small"
                recordItemId={record.id}
                resource="sourcings"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
